import type {
  AcaoRemota,
  AssinaturaJunta,
  CertificadoInfo,
  ConfiguracaoEquipe,
  DetalheAbertura,
  Estado,
  Etapa,
  ExecucaoAgente,
  Fila,
  MensagemPronta,
  NovaDemanda,
  Prioridade,
  ResumoAgente,
  Situacao,
  TaxaJunta,
  TelaNavegador,
  UsuarioEquipe,
} from '@contefy/shared';
import { ErroApi } from '../lib/api';

const BASE = import.meta.env.VITE_API_URL ?? '/api';
const CHAVE_TOKEN = 'contefy.equipe.token';
const CHAVE_API = 'contefy.equipe.apikey';

/** Guarda a credencial da sessão: token do WordPress (vem no #token= da URL) ou chave técnica. */
export const sessao = {
  capturarDaUrl(): boolean {
    const m = /(?:^|[#&])token=([^&]+)/.exec(window.location.hash);
    if (!m) return false;
    try {
      sessionStorage.setItem(CHAVE_TOKEN, decodeURIComponent(m[1]!));
      sessionStorage.removeItem(CHAVE_API);
    } catch {
      /* armazenamento indisponível: a sessão dura só esta página */
    }
    history.replaceState(null, '', window.location.pathname + window.location.search);
    return true;
  },
  token(): string | null {
    try {
      return sessionStorage.getItem(CHAVE_TOKEN);
    } catch {
      return null;
    }
  },
  chaveApi(): string | null {
    try {
      return sessionStorage.getItem(CHAVE_API);
    } catch {
      return null;
    }
  },
  usarChaveApi(chave: string) {
    try {
      sessionStorage.setItem(CHAVE_API, chave);
      sessionStorage.removeItem(CHAVE_TOKEN);
    } catch {
      /* ignora */
    }
  },
  sair() {
    try {
      sessionStorage.removeItem(CHAVE_TOKEN);
      sessionStorage.removeItem(CHAVE_API);
    } catch {
      /* ignora */
    }
  },
  temCredencial(): boolean {
    return Boolean(sessao.token() || sessao.chaveApi());
  },
};

/** Envio de arquivo: sem Content-Type fixo (o navegador monta o multipart). */
async function chamarForm<T>(caminho: string, form: FormData): Promise<T> {
  const headers: Record<string, string> = {};
  const token = sessao.token();
  const chave = sessao.chaveApi();
  if (token) headers.Authorization = `Bearer ${token}`;
  else if (chave) headers['X-Api-Key'] = chave;
  const r = await fetch(`${BASE}/equipe${caminho}`, { method: 'POST', body: form, headers });
  const texto = await r.text();
  const corpo = texto ? (JSON.parse(texto) as Record<string, unknown>) : {};
  if (!r.ok) {
    if (r.status === 401) sessao.sair();
    throw new ErroApi(
      (corpo.mensagem as string) ?? (corpo.message as string) ?? 'Algo deu errado.',
      r.status,
      [],
    );
  }
  return corpo as T;
}

async function chamar<T>(caminho: string, init: RequestInit = {}): Promise<T> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(init.headers as Record<string, string>),
  };
  const token = sessao.token();
  const chave = sessao.chaveApi();
  if (token) headers.Authorization = `Bearer ${token}`;
  else if (chave) headers['X-Api-Key'] = chave;
  const r = await fetch(`${BASE}/equipe${caminho}`, { ...init, headers });
  const texto = await r.text();
  const corpo = texto ? (JSON.parse(texto) as Record<string, unknown>) : {};
  if (!r.ok) {
    if (r.status === 401) sessao.sair();
    const mensagem = (corpo.mensagem as string) ?? (corpo.message as string) ?? 'Algo deu errado.';
    throw new ErroApi(
      mensagem,
      r.status,
      (corpo.erros as { campo: string; mensagem: string }[]) ?? [],
    );
  }
  return corpo as T;
}

export const equipeApi = {
  eu: () => chamar<UsuarioEquipe>('/eu'),
  fila: (f: {
    estado?: string;
    responsavel?: string;
    so_minhas?: boolean;
    tipo?: string;
    prioridade?: string;
  }) => {
    const q = new URLSearchParams();
    if (f.estado) q.set('estado', f.estado);
    if (f.responsavel) q.set('responsavel', f.responsavel);
    if (f.so_minhas) q.set('so_minhas', '1');
    if (f.tipo) q.set('tipo', f.tipo);
    if (f.prioridade) q.set('prioridade', f.prioridade);
    const s = q.toString();
    return chamar<Fila>(`/fila${s ? `?${s}` : ''}`);
  },
  criarDemanda: (dados: NovaDemanda) =>
    chamar<{ id: string }>('/aberturas', { method: 'POST', body: JSON.stringify(dados) }),
  prioridade: (id: string, prioridade: Prioridade) =>
    chamar<{ prioridade: Prioridade }>(`/aberturas/${id}/prioridade`, {
      method: 'PUT',
      body: JSON.stringify({ prioridade }),
    }),
  anexarGuia: (id: string, arquivo: File) => {
    const form = new FormData();
    form.append('arquivo', arquivo);
    return chamarForm<TaxaJunta>(`/aberturas/${id}/taxa/guia`, form);
  },
  assinatura: (id: string, dados: { link?: string | null; processo?: string | null }) =>
    chamar<AssinaturaJunta>(`/aberturas/${id}/assinatura`, {
      method: 'PUT',
      body: JSON.stringify(dados),
    }),
  modelosAssinatura: () => chamar<Record<Estado, string>>('/mensagens/assinatura'),
  salvarModelosAssinatura: (textos: Partial<Record<Estado, string>>) =>
    chamar<Record<Estado, string>>('/mensagens/assinatura', {
      method: 'PUT',
      body: JSON.stringify(textos),
    }),
  detalhe: (id: string) => chamar<DetalheAbertura>(`/aberturas/${id}`),
  corrigir: (id: string, chave: string, valor: unknown) =>
    chamar(`/aberturas/${id}/ficha/${encodeURIComponent(chave)}`, {
      method: 'PATCH',
      body: JSON.stringify({ valor }),
    }),
  aceitar: (id: string, chave: string) =>
    chamar(`/aberturas/${id}/ficha/${encodeURIComponent(chave)}/aceitar`, { method: 'POST' }),
  aprovar: (id: string, contrato: 'padrao' | 'proprio', observacao?: string) =>
    chamar(`/aberturas/${id}/aprovar`, {
      method: 'POST',
      body: JSON.stringify({ contrato, observacao }),
    }),
  remontar: (id: string) => chamar(`/aberturas/${id}/remontar`, { method: 'POST' }),
  etapa: (id: string, etapa: Etapa, motivo: string, situacao?: Situacao) =>
    chamar(`/aberturas/${id}/etapa`, {
      method: 'POST',
      body: JSON.stringify({ etapa, motivo, situacao }),
    }),
  situacao: (id: string, situacao: Situacao, motivo: string) =>
    chamar(`/aberturas/${id}/situacao`, {
      method: 'POST',
      body: JSON.stringify({ situacao, motivo }),
    }),
  responsavel: (id: string, responsavel: string | null) =>
    chamar(`/aberturas/${id}/responsavel`, {
      method: 'POST',
      body: JSON.stringify({ responsavel }),
    }),
  nota: (id: string, texto: string) =>
    chamar(`/aberturas/${id}/notas`, { method: 'POST', body: JSON.stringify({ texto }) }),
  conferirDocumento: (
    id: string,
    documentoId: string,
    status: 'aceito' | 'rejeitado',
    motivo?: string,
  ) =>
    chamar(`/aberturas/${id}/documentos/${documentoId}/conferir`, {
      method: 'POST',
      body: JSON.stringify({ status, motivo }),
    }),
  urlDocumento: (id: string, documentoId: string) =>
    chamar<{ url: string; mime: string }>(`/aberturas/${id}/documentos/${documentoId}/url`),
  salvarTaxa: (id: string, dados: Partial<TaxaJunta>) =>
    chamar<TaxaJunta>(`/aberturas/${id}/taxa`, { method: 'PUT', body: JSON.stringify(dados) }),
  mensagensProntas: (id: string) => chamar<MensagemPronta[]>(`/aberturas/${id}/mensagens-prontas`),
  mensagemAberta: (
    id: string,
    dados: { modelo: string; canal: 'whatsapp' | 'email'; destinatario: string },
  ) =>
    chamar<{ ok: true }>(`/aberturas/${id}/mensagens-prontas/aberta`, {
      method: 'POST',
      body: JSON.stringify(dados),
    }),
  certificado: () =>
    chamar<CertificadoInfo | Record<string, never> | null>('/certificado').then((d) =>
      d && 'titular' in d ? (d as CertificadoInfo) : null,
    ),
  enviarCertificado: (arquivo: File, senha: string) => {
    const form = new FormData();
    form.append('arquivo', arquivo);
    form.append('senha', senha);
    return chamarForm<CertificadoInfo>('/certificado', form);
  },
  removerCertificado: () => chamar<{ ok: true }>('/certificado', { method: 'DELETE' }),
  agentes: () => chamar<ResumoAgente[]>('/agentes'),
  agentesDaAbertura: (id: string) => chamar<ExecucaoAgente[]>(`/aberturas/${id}/agentes`),
  executarAgente: (id: string, agente: string, objetivo: string) =>
    chamar<{ execucaoId: string; status: string }>(`/aberturas/${id}/agentes/${agente}/executar`, {
      method: 'POST',
      body: JSON.stringify({ objetivo }),
    }),
  execucao: (id: string) => chamar<ExecucaoAgente>(`/agentes/execucoes/${id}`),
  telaNavegador: (execucaoId: string) =>
    chamar<TelaNavegador & { pode_retomar: boolean }>(`/agentes/execucoes/${execucaoId}/navegador`),
  acaoNavegador: (execucaoId: string, acao: AcaoRemota) =>
    chamar<TelaNavegador>(`/agentes/execucoes/${execucaoId}/navegador/acao`, {
      method: 'POST',
      body: JSON.stringify(acao),
    }),
  retomarExecucao: (execucaoId: string, nota?: string) =>
    chamar<{ ok: true; status: string }>(`/agentes/execucoes/${execucaoId}/retomar`, {
      method: 'POST',
      body: JSON.stringify({ nota: nota || undefined }),
    }),
  esquecerSessaoGovbr: () =>
    chamar<{ ok: true }>('/certificado/sessao-govbr', { method: 'DELETE' }),
  configuracoes: () => chamar<ConfiguracaoEquipe[]>('/configuracoes'),
  salvarConfiguracao: (chave: string, valor: unknown, descricao?: string) =>
    chamar<ConfiguracaoEquipe>(`/configuracoes/${encodeURIComponent(chave)}`, {
      method: 'PUT',
      body: JSON.stringify({ valor, descricao }),
    }),
};

export function diasDesde(iso: string): number {
  return Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000);
}

export function dataHora(iso: string | null | undefined): string {
  if (!iso) return '';
  return new Date(iso).toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function rotuloValor(v: unknown): string {
  if (v == null || v === '') return '—';
  if (typeof v === 'boolean') return v ? 'Sim' : 'Não';
  if (typeof v === 'number') return v.toLocaleString('pt-BR');
  if (Array.isArray(v)) return v.map(rotuloValor).join(' · ');
  if (typeof v === 'object') {
    const o = v as Record<string, unknown>;
    if ('codigo' in o && 'descricao' in o)
      return `${o.codigo} ${o.descricao}${o.principal ? ' (principal)' : ''}`;
    return JSON.stringify(v);
  }
  return String(v);
}
